import { useState } from "react";
import { IconButton, Stack, Title } from "@introist/react-foundation/v2";
import { Skeletons } from "components/molecules/Skeletons";
import { List, ListRow } from "components/organisms/List";
import { StepEditorAccordion } from "modules/workflows/routes/WorkflowEditor/StepEditorAccordion";
import { ReadOnlyHTMLEditor } from "./ReadOnlyHTMLEditor";
import { api } from "services/rpc/RpcProvider";

interface StepReminder {
  maxCount: number;
  intervalDays: number;
}

interface Props {
  stepId: string;
  reminderConfig: StepReminder;
}

const formatDate = (date: string | Date): string => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const ReminderRow = ({ sequenceNumber, sentAt, message }: { sequenceNumber: number; sentAt: string | Date; message: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ListRow gridTemplateColumns="1fr auto auto">
        <Title variant="bold">Reminder #{sequenceNumber}</Title>
        <Title>{formatDate(sentAt)}</Title>
        <IconButton
          size="small"
          variant="blended"
          icon={open ? "chevronUp" : "chevronDown"}
          onClick={() => setOpen(prev => !prev)}
        />
      </ListRow>
      {open && (
        <ListRow gridTemplateColumns="1fr">
          <ReadOnlyHTMLEditor html={message} />
        </ListRow>
      )}
    </>
  );
};

export const StepReminderViewer = ({ stepId, reminderConfig }: Props) => {
  const { data: sends, isLoading } = api.automations.steps.listReminderSends.useQuery({ stepId });

  return (
    <StepEditorAccordion title="Reminders">
      <Stack vertical gap="small">
        <Title>
          Send up to {reminderConfig.maxCount} times, every {reminderConfig.intervalDays} days
        </Title>
        {isLoading && <Skeletons.List rowCount={3} rowHeight={40} />}
        {!isLoading && sends && sends.length === 0 && <Title>No reminders sent yet</Title>}
        {!isLoading && sends && sends.length > 0 && (
          <List>
            {sends.map(send => (
              <ReminderRow
                key={send.id}
                sequenceNumber={send.sequenceNumber}
                sentAt={send.sentAt}
                message={send.message}
              />
            ))}
          </List>
        )}
      </Stack>
    </StepEditorAccordion>
  );
};
